import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavArrowLeft, NavArrowRight, Plus } from 'iconoir-react';
import { cn } from 'lib/utils';
import { useTheme } from 'components/core/theme-provider';
import { useTranslation } from 'react-i18next';
import { useGetAgents } from '../hooks/use-agents';
import { useNavigate } from 'react-router-dom';
import { useConfig } from 'hooks/use-config';

const CARD_WIDTH_CLASS = 'w-[96px] shrink-0 sm:w-[129px]';

const CREATE_CARD_CLASS = cn(
  CARD_WIDTH_CLASS,
  'flex h-[118px] flex-col items-center gap-1.5 rounded-[10px] border border-[#e7e7e7] bg-white px-2 pb-2 pt-3 sm:h-[159px] sm:gap-4 sm:rounded-[12px] sm:px-6 sm:pb-4 sm:pt-6'
);

const AGENT_CARD_CLASS = cn(
  CARD_WIDTH_CLASS,
  'relative h-[118px] overflow-hidden rounded-[10px] sm:h-[159px] sm:rounded-[12px] border border-[#e7e7e7] bg-gradient-to-br from-primary/10 to-primary/5'
);

const AGENT_CARD_LABEL_CLASS =
  'absolute bottom-1.5 left-2 right-2 z-[1] font-sans text-[10px] font-medium not-italic leading-[12px] text-foreground sm:bottom-2 sm:left-[9.801px] sm:right-[9.801px] sm:text-xs sm:leading-[14.4px] truncate max-w-[calc(100%-1rem)] sm:max-w-[calc(100%-1.25rem)]';

const MORE_CARD_CLASS = cn(
  CARD_WIDTH_CLASS,
  'flex h-[118px] flex-col items-center justify-center gap-1.5 rounded-[10px] border border-[#e7e7e7] bg-white px-2 pb-2 pt-3 sm:h-[159px] sm:gap-4 sm:rounded-[12px]'
);

interface AgentsSectionProps {
  className?: string;
}

export function AgentsSection({ className }: AgentsSectionProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { configurationSettings } = useConfig();
  const projectKey = configurationSettings?.blocksKey || '';

  const { data: agentsData, isLoading } = useGetAgents(
    { offset: 0, limit: 20, project_key: projectKey },
    { enabled: !!projectKey }
  );

  const agents = agentsData?.agents || [];
  const totalAgents = agentsData?.total_count || 0;

  const updateScrollState = useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  const handleScroll = (direction: 'left' | 'right') => {
    const cardStep = scrollRef.current?.firstElementChild?.clientWidth ?? 129;
    scrollRef.current?.scrollBy({
      left: direction === 'left' ? -(cardStep + 8) : cardStep + 8,
      behavior: 'smooth',
    });
  };

  const handleCreateAgent = () => {
    navigate('/agents?create=true');
  };

  const handleAgentClick = (agentId: string) => {
    navigate(`/chat?agentId=${encodeURIComponent(agentId)}`);
  };

  const handleMoreClick = () => {
    navigate('/agents');
  };

  const iconColor = theme === 'dark' ? '#F6F6F6' : '#3D3D3D';
  const headerTextClass = theme === 'dark' ? 'text-[#F6F6F6]' : 'text-[#3D3D3D]';

  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [updateScrollState]);

  // Show all available agents up to limit, but cap at a reasonable number for scrolling
  const maxDisplayAgents = Math.min(agents.length, 15);
  const displayAgents = agents.slice(0, maxDisplayAgents);
  const hasMore = totalAgents > agents.length;

  return (
    <div className={cn('flex w-full min-w-0 flex-col gap-3', className)}>
      <div className="flex w-full min-w-0 items-center justify-between gap-3">
        <h3
          className={cn(
            'shrink-0 font-sans text-lg font-medium not-italic leading-[27px] md:text-xl md:leading-[30px]',
            headerTextClass
          )}
        >
          {t('AGENTS', { defaultValue: 'Agents' })}
        </h3>

        <div className="hidden shrink-0 items-center gap-2 md:flex md:gap-2.5">
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              aria-label={t('SCROLL_LEFT', { defaultValue: 'Scroll left' })}
              disabled={!canScrollLeft}
              onClick={() => handleScroll('left')}
              className="flex size-6 items-center justify-center disabled:opacity-30"
            >
              <NavArrowLeft width={24} height={24} color={iconColor} />
            </button>
            <button
              type="button"
              aria-label={t('SCROLL_RIGHT', { defaultValue: 'Scroll right' })}
              disabled={!canScrollRight}
              onClick={() => handleScroll('right')}
              className="flex size-6 items-center justify-center disabled:opacity-30"
            >
              <NavArrowRight width={24} height={24} color={iconColor} />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        className="flex w-full min-w-0 gap-1.5 overflow-x-auto overscroll-x-contain pb-1 sm:gap-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <button
          type="button"
          onClick={handleCreateAgent}
          className={cn(
            CREATE_CARD_CLASS,
            theme === 'dark' && 'border-gray-500 bg-black-400'
          )}
        >
          <span className="flex min-h-0 w-full flex-1 items-center justify-center">
            <span className="flex size-10 items-center justify-center rounded-full border border-gray-300 bg-gray-100 dark:border-gray-600 dark:bg-white/10 sm:size-12">
              <Plus width={20} height={20} color={iconColor} className="size-5 sm:size-6" />
            </span>
          </span>
        </button>

        {isLoading ? (
          Array.from({ length: 5 }).map((_, index) => (
            <div
              key={`skeleton-${index}`}
              className={cn(
                AGENT_CARD_CLASS,
                theme === 'dark' && 'border-gray-500 bg-gradient-to-br from-muted/50 to-muted/30',
                !theme || theme === 'light' ? 'bg-gradient-to-br from-gray-200 to-gray-100' : ''
              )}
            >
              <div className="flex size-full flex-col items-center justify-center p-2">
                <div className={cn(
                  "size-10 sm:size-12 rounded-full animate-pulse",
                  theme === 'dark' ? 'bg-muted/60' : 'bg-gray-300'
                )} />
              </div>
              <div className={cn(
                "absolute bottom-1.5 left-2 right-2 h-3 sm:h-3.5 rounded animate-pulse",
                theme === 'dark' ? 'bg-muted/60' : 'bg-gray-300'
              )} />
            </div>
          ))
        ) : (
          displayAgents.map((agent) => (
            <button
              key={agent.id}
              type="button"
              onClick={() => handleAgentClick(agent.id)}
              className={cn(
                AGENT_CARD_CLASS,
                theme === 'dark' && 'border-gray-500 bg-gradient-to-br from-primary/20 to-primary/10'
              )}
            >
              <div className="flex size-full flex-col items-center justify-center p-2">
                {agent.logo_url ? (
                  <img
                    src={agent.logo_url}
                    alt={agent.name}
                    className="size-10 rounded-full object-cover sm:size-12"
                  />
                ) : (
                  <div className="flex size-10 items-center justify-center rounded-full border border-gray-300 bg-gray-100 dark:border-gray-600 dark:bg-white/10 sm:size-12">
                    <span className="text-lg font-semibold text-foreground sm:text-xl">
                      {agent.name?.[0]?.toUpperCase() || 'A'}
                    </span>
                  </div>
                )}
              </div>
              <span className={AGENT_CARD_LABEL_CLASS}>
                {agent.name}
              </span>
            </button>
          ))
        )}

        {hasMore && (
          <button
            type="button"
            onClick={handleMoreClick}
            className={cn(
              MORE_CARD_CLASS,
              theme === 'dark' && 'border-gray-500 bg-black-400'
            )}
          >
            <span className="shrink-0 text-center text-[10px] leading-[1.2] text-[#3d3d3d] dark:text-foreground sm:whitespace-nowrap sm:text-xs font-medium">
              {t('MORE', { defaultValue: 'More' })}
            </span>
            <span className="text-xs text-muted-foreground">
              +{totalAgents - agents.length}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
